/**
 * Export Handler - Dashboard Screenshot Capture
 *
 * Listens for postMessage requests from the RevenuePipelineOS shell
 * and captures the dashboard as a PNG screenshot.
 *
 * Usage: Import useExportHandler() in App.js
 *
 * @see RPOS Dev Team Request - December 2025
 */

import { useEffect } from 'react';
import html2canvas from 'html2canvas';

// Allowed origins that can trigger export
const ALLOWED_ORIGINS = [
  'https://revenuepipelineos.com'
];

/**
 * React Hook - Sets up postMessage listener for export requests
 * Call this once in App.js to enable export functionality
 */
export function useExportHandler() {
  useEffect(() => {
    const handleMessage = async (event) => {
      // Security: Only accept messages from allowed origins
      // Note: For local console testing, origin check is bypassed when using postMessage to self
      if (!ALLOWED_ORIGINS.includes(event.origin) && event.origin !== window.location.origin) {
        return;
      }

      // Handle export request
      if (event.data?.type === 'EXPORT_REQUEST') {
        try {
          console.log('📸 Export request received, capturing dashboard...');

          // Send acknowledgment back to shell
          if (event.source && event.origin !== window.location.origin) {
            event.source.postMessage({ type: 'EXPORT_STARTED' }, event.origin);
          }

          // Capture the full page
          const canvas = await html2canvas(document.body, {
            scrollX: 0,
            scrollY: 0,
            windowWidth: document.documentElement.scrollWidth,
            windowHeight: document.documentElement.scrollHeight,
            useCORS: true,
            allowTaint: false,
            scale: 2,
            logging: false
          });

          // Generate filename with timestamp
          const timestamp = new Date().toISOString().split('T')[0];
          const filename = `performance-dashboard-${timestamp}.png`;

          // Trigger download
          const link = document.createElement('a');
          link.download = filename;
          link.href = canvas.toDataURL('image/png');
          link.click();

          console.log(`✅ Export complete: ${filename}`);

          // Send completion message back to shell
          if (event.source && event.origin !== window.location.origin) {
            event.source.postMessage({ type: 'EXPORT_COMPLETE' }, event.origin);
          }

        } catch (error) {
          console.error('❌ Export failed:', error);

          // Send error message back to shell
          if (event.source && event.origin !== window.location.origin) {
            event.source.postMessage({
              type: 'EXPORT_ERROR',
              error: error.message
            }, event.origin);
          }
        }
      }

      // Handle region-capture request from the Report-an-Issue widget
      // (cross-origin bridge — the shell can't html2canvas our pixels, so we
      // capture our own document.body region and post the PNG dataURL back).
      // x/y arrive as THIS frame's viewport CSS px; add our own scroll offset.
      if (event.data?.type === 'CAPTURE_REQUEST' && event.data?.source === 'rpos-issue-capture') {
        const { id, x, y, w, h } = event.data;
        try {
          const canvas = await html2canvas(document.body, {
            x: x + window.scrollX,
            y: y + window.scrollY,
            width: w,
            height: h,
            useCORS: true,
            allowTaint: false,
            scale: 1,
            logging: false
          });
          const dataUrl = canvas.toDataURL('image/png');
          if (event.source) {
            event.source.postMessage(
              { source: 'rpos-issue-capture', type: 'CAPTURE_RESULT', id, dataUrl },
              event.origin
            );
          }
        } catch (error) {
          if (event.source) {
            event.source.postMessage(
              { source: 'rpos-issue-capture', type: 'CAPTURE_ERROR', id, error: error?.message || 'capture failed' },
              event.origin
            );
          }
        }
      }
    };

    // Add listener
    window.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
}
